// Explore-stats page: Ministerial statement statistics.
// Recreates the Handbook page — ministerial statements over time (per year and
// per Parliament) plus breakdowns by minister and by portfolio. Derived from
// the `hansard_toc` table-of-contents (level-1 "MINISTERIAL STATEMENTS"
// debates and their speakers), with the speaker PHID joined to ministry
// records for the portfolio breakdown.

import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { getExploreStat } from '../../services/api';
import ExploreStatsPage from './ExploreStatsPage';
import { StatLine, StatBar } from './charts';

export default function MinisterialStatements() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await getExploreStat('ministerial-statements');
        if (active) setData(res);
      } catch (e) {
        if (active) setError(e.message || 'Failed to load statistics');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const c = data?.charts || {};

  return (
    <ExploreStatsPage
      title="Ministerial statement statistics"
      description={`Ministerial statements made in the Parliament${
        c.total ? ` (${c.total} statements)` : ''
      }, derived from the Hansard table of contents. Coverage currently spans 2000–2025 (densest 2000–2010) and grows automatically as the Hansard TOC backfill extends. Portfolio is derived by matching the speaker to the ministry record active on the statement date; statements made by speakers without a matching ministry record (e.g. opposition or cross-bench responses) are excluded from the portfolio breakdown but still counted everywhere else.`}
      loading={loading}
      error={error}
      columns={data?.columns || []}
      rows={data?.table_rows || []}
      links={data?._links || []}
      csvName="ministerial-statements"
    >
      {data && (
        <>
          <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Ministerial statements per year
            </Typography>
            <StatLine data={c.per_year || []} xKey="year" keys={['count']} />
          </Paper>

          <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Ministerial statements per Parliament
            </Typography>
            <StatLine data={c.per_parliament || []} xKey="parliament"
                      keys={['count']} />
          </Paper>

          <Box sx={{ display: 'grid', gap: 3, mb: 3,
                     gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
            <Paper elevation={1} sx={{ p: 2, borderRadius: 3 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Top 15 by minister
              </Typography>
              <StatBar data={c.by_minister || []} xKey="name" yKey="count"
                       layout="vertical" height={420} />
            </Paper>
            <Paper elevation={1} sx={{ p: 2, borderRadius: 3 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Top 15 by portfolio
              </Typography>
              <StatBar data={c.by_portfolio || []} xKey="portfolio" yKey="count"
                       layout="vertical" height={420} />
            </Paper>
          </Box>
        </>
      )}
    </ExploreStatsPage>
  );
}
